import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HpoReleaseNote from './HpoReleaseNote';
import { memberKey, memberLabel } from './clinicalSuggestions/relativeMatch';
import { TILE_COLOURS } from 'src/components/pedigree-chart/pedigree-sidebar/sections/tileColours';

// Suggested Clinical Terms, grouped by PERSON (TGCLOUD-1845, decision 6).
//
// Each finding shows how the document put it (a status badge), the words it
// came from, and — for a relative — who in the pedigree it will be added to.
// Only definite findings start ticked; an uncertain, second-hand or suspected
// one, or a variant of uncertain significance, is shown but left for the
// clinician to tick.

// Each kind wears the register its records wear everywhere else: the sidebar's
// record tiles and the legend drag chips (sections/tileColours.js). The
// genotype pill used to be #2e7d32, the "clinically confirmed" green, beside a
// green Confirmed badge.
export const KIND = {
  phenotype: { label: 'Phenotype', register: TILE_COLOURS.phenotype },
  disease: { label: 'Disease', register: TILE_COLOURS.disease },
  genotype: { label: 'Genotype', register: TILE_COLOURS.genotype }
};

const STATUS = {
  absent: {
    label: 'Absent',
    color: 'error',
    hint: 'The document says this is NOT present. Added as an excluded term.'
  },
  uncertain: {
    label: 'Uncertain',
    color: 'warning',
    hint: 'The document hedges this. Added with a note saying so.'
  },
  suspected: {
    label: 'Suspected',
    color: 'warning',
    hint: 'Under consideration, not confirmed. Added with a note saying so.'
  },
  reported: {
    label: 'Second-hand',
    color: 'secondary',
    hint: 'Reported by someone else, not observed. Added with a note saying so.'
  },
  past: { label: 'Past', color: 'default', hint: 'Resolved or in the past.' },
  confirmed: {
    label: 'Confirmed',
    color: 'success',
    hint: 'The document confirms this diagnosis.'
  }
};

const ageText = (age) =>
  age === null || age === undefined || age === ''
    ? null
    : typeof age === 'number'
    ? `age ${age}`
    : `age ${age}`;

function FindingRow({ subjectKey, finding: f, checked, disabled, onToggle }) {
  const kind = KIND[f.kind] || KIND.phenotype;
  const status = STATUS[f.status];
  const vus =
    f.kind === 'genotype' && f.classification === 'Uncertain significance';
  return (
    <Box
      data-testid={`finding-${subjectKey}-${f.id}`}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}
    >
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        inputProps={{ 'aria-label': `Add ${f.name}` }}
        sx={{ p: 0.25, mt: -0.25 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {f.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {f.id}
          </Typography>
          <Chip
            label={kind.label}
            size="small"
            variant="outlined"
            data-testid={`kind-${f.kind}`}
            sx={{
              height: 18,
              fontSize: '0.68rem',
              backgroundColor: kind.register.background,
              borderColor: kind.register.border,
              color: kind.register.text
            }}
          />
          {status && (
            <Tooltip title={status.hint} arrow>
              <Chip
                label={status.label}
                size="small"
                color={status.color}
                sx={{ height: 18, fontSize: '0.68rem' }}
              />
            </Tooltip>
          )}
          {ageText(f.age) && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {ageText(f.age)}
            </Typography>
          )}
          {f.source === 'ai' && (
            <Tooltip
              title="Found by AI, not by the vocabulary match. The quote is from the document; check the term before adding it."
              arrow
            >
              <Chip
                label="AI"
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.68rem' }}
              />
            </Tooltip>
          )}
          {f.alreadyRecorded && (
            <Chip
              label="Already recorded"
              size="small"
              sx={{ height: 18, fontSize: '0.68rem' }}
            />
          )}
        </Box>
        {f.kind === 'genotype' && f.variant && (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary' }}
          >
            {[
              f.variant.transcript && f.variant.hgvsC
                ? `${f.variant.transcript}:${f.variant.hgvsC}`
                : f.variant.hgvsC,
              f.variant.hgvsP,
              f.variant.zygosity
            ]
              .filter(Boolean)
              .join(' · ')}
            {f.classification ? ` · ${f.classification}` : ''}
            {vus ? ' — not for predictive testing or diagnosis' : ''}
            {f.notSighted ? ' · report not sighted' : ''}
          </Typography>
        )}
        {f.kind === 'disease' && (f.support?.gene || f.support?.phenotypes) ? (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary' }}
          >
            {[
              f.support.gene
                ? `linked to the ${f.support.gene} variant reported`
                : null,
              f.support.phenotypes
                ? `${f.support.phenotypes} phenotype${
                    f.support.phenotypes === 1 ? '' : 's'
                  } here match its HPO annotations`
                : null
            ]
              .filter(Boolean)
              .join('; ')}
          </Typography>
        ) : null}
        {f.evidence?.[0] && (
          <Typography
            variant="caption"
            component="div"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
              overflowWrap: 'anywhere'
            }}
          >
            “{f.evidence[0]}”
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function TargetPicker({ subject, match, value, onChange, disabled }) {
  const fitting = match?.candidates || [];
  const others = match?.others || [];
  const label = (c) =>
    `${memberLabel(c.member)}${c.relation ? ` — ${c.relation}` : ''}`;
  return (
    <FormControl size="small" sx={{ minWidth: 220 }} disabled={disabled}>
      <Select
        displayEmpty
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': `Add ${subject.label} findings to` }}
        renderValue={(v) => {
          if (!v) return <em>Don’t add</em>;
          const c = [...fitting, ...others].find(
            (x) => memberKey(x.member) === v
          );
          return c ? label(c) : v;
        }}
      >
        <MenuItem value="">
          <em>Don’t add</em>
        </MenuItem>
        {fitting.length > 0 && (
          <ListSubheader>Fits “{subject.label.toLowerCase()}”</ListSubheader>
        )}
        {fitting.map((c) => (
          <MenuItem key={memberKey(c.member)} value={memberKey(c.member)}>
            {label(c)}
          </MenuItem>
        ))}
        {others.length > 0 && (
          <ListSubheader>Someone else in this family</ListSubheader>
        )}
        {others.map((c) => (
          <MenuItem key={memberKey(c.member)} value={memberKey(c.member)}>
            {label(c)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// '“a.pdf”', '“a.pdf” and “b.pdf”', '“a.pdf”, “b.pdf” and “c.pdf”'.
const quotedList = (names) => {
  const q = names.map((n) => `“${n}”`);
  return q.length > 1
    ? `${q.slice(0, -1).join(', ')} and ${q[q.length - 1]}`
    : q[0];
};

// With several documents uploaded, the warning names the ones it is about
// (`check.sources`, the files whose own check found a mismatch — TGCLOUD-1850).
function IdentityWarning({ check }) {
  if (!check || !(check.dobMismatch || check.surnameMismatch)) return null;
  const sources = [...new Set(check.sources || [])];
  if (sources.length > 1) {
    return (
      <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="identity-warning">
        {quotedList(sources)} may be about someone else: the date of birth or
        name in each does not match this record. Check they are the right
        documents before adding anything.
      </Alert>
    );
  }
  const parts = [];
  if (check.dobMismatch) parts.push(`date of birth (${check.documentDob})`);
  if (check.surnameMismatch) parts.push(`name (${check.documentName})`);
  return (
    <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="identity-warning">
      {sources.length ? quotedList(sources) : 'This document'} may be about
      someone else: its {parts.join(' and ')} {parts.length > 1 ? 'do' : 'does'}{' '}
      not match this record. Check it is the right document before adding
      anything.
    </Alert>
  );
}

function TerminologyNotice({ terminology }) {
  if (!terminology || terminology.available !== false) return null;
  const off = terminology.reason === 'system-disabled';
  return (
    <Alert severity={off ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
      {off
        ? 'HPO is switched off in Settings, so no phenotypes or diseases are suggested.'
        : 'The terminology service could not be reached, so phenotypes and diseases were not looked up. This is not the same as “nothing found”.'}
    </Alert>
  );
}

const FAMILY_NOTE_STATUS = {
  absent: 'not present',
  'not detected': 'not detected'
};

// 'family' → "Family"; 'maternal aunt:Linda' → "Maternal aunt (Linda)".
function noteSubject(key) {
  if (!key || key === 'family') return 'Family';
  const [rel, name] = String(key).split(':');
  const label =
    rel === 'self'
      ? 'This patient'
      : rel.charAt(0).toUpperCase() + rel.slice(1);
  return name ? `${label} (${name})` : label;
}

export default function ClinicalSuggestionsPanel({
  state,
  onAccept,
  disabled = false
}) {
  if (!state) return null;
  const {
    analysis,
    analyzingNote,
    matches,
    targetFor,
    setTarget,
    isSelected,
    toggleFinding,
    selectedCount,
    savingNote,
    lastAccept,
    hpoRelease
  } = state;
  const subjects = (analysis?.subjects || []).filter((s) => s.findings.length);
  const notes = analysis?.familyNotes || [];
  const hasAny = subjects.length || notes.length;
  const notice =
    analysis?.terminology?.available === false || analysis?.identityCheck;
  if (
    disabled ||
    (!hasAny && !analyzingNote && !notice && !lastAccept?.failed?.length)
  )
    return null;

  const hpoDerived = subjects.some((s) =>
    s.findings.some((f) => f.kind !== 'genotype')
  );

  return (
    <Grid size={12}>
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {analyzingNote ? (
            <>
              <CircularProgress size={16} />
              Analyzing…
            </>
          ) : (
            'Suggested Clinical Terms'
          )}
          {!analyzingNote && analysis?.engine && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 400 }}
            >
              {analysis.engine === 'vocabulary+ai'
                ? 'found by matching the document against HPO, OMIM and Orphanet; those marked AI were added by AI and checked against the document'
                : 'found by matching the document against HPO, OMIM and Orphanet'}
            </Typography>
          )}
        </Typography>

        <IdentityWarning check={analysis?.identityCheck} />
        <TerminologyNotice terminology={analysis?.terminology} />
        {lastAccept?.failed?.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {lastAccept.failed.length}{' '}
            {lastAccept.failed.length === 1 ? 'person was' : 'people were'} not
            found in this family’s saved pedigree, so nothing was added for
            them. Save the pedigree and try again.
          </Alert>
        )}

        {subjects.map((s, i) => {
          const target = targetFor(s.key);
          const relative = s.key !== 'self';
          const match = matches?.[s.key];
          const noCandidates =
            relative && !(match?.candidates?.length || match?.others?.length);
          return (
            <Box key={s.key} sx={{ mb: 1.5 }} data-testid={`subject-${s.key}`}>
              {i > 0 && <Divider sx={{ mb: 1 }} />}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 0.5
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {s.label}
                  {s.name ? ` (${s.name})` : ''}
                </Typography>
                {relative && s.relation !== 'partner' && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      add to:
                    </Typography>
                    <TargetPicker
                      subject={s}
                      match={match}
                      value={target}
                      onChange={(v) => setTarget(s.key, v)}
                      disabled={savingNote}
                    />
                  </>
                )}
              </Box>
              {noCandidates && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  No one saved in this pedigree can take these yet. Add this
                  relative to the pedigree and save it to record them.
                </Typography>
              )}
              {s.relation === 'partner' && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: 'text.secondary', mb: 0.5 }}
                >
                  Not a blood relative — shown for information only.
                </Typography>
              )}
              {s.findings.map((f) => (
                <FindingRow
                  key={`${f.kind}-${f.id}`}
                  subjectKey={s.key}
                  finding={f}
                  checked={isSelected(s.key, f) && !!target}
                  disabled={
                    savingNote ||
                    f.alreadyRecorded ||
                    !target ||
                    s.relation === 'partner'
                  }
                  onToggle={() => toggleFinding(s.key, f)}
                />
              ))}
            </Box>
          );
        })}

        {notes.length > 0 && (
          <Box sx={{ mt: 1 }} data-testid="family-notes">
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Also in the document (not added to anyone)
            </Typography>
            {notes.map((n, idx) => (
              <Typography
                key={`${n.name}-${idx}`}
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary' }}
              >
                {noteSubject(n.subject)}: {n.name}
                {FAMILY_NOTE_STATUS[n.status]
                  ? ` — ${FAMILY_NOTE_STATUS[n.status]}`
                  : n.status
                  ? ` — ${n.status}`
                  : ''}
              </Typography>
            ))}
          </Box>
        )}

        {!analyzingNote && selectedCount > 0 && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={onAccept}
              disabled={savingNote}
              startIcon={
                savingNote ? <CircularProgress size={16} /> : <AddIcon />
              }
            >
              Add {selectedCount} selected
            </Button>
          </Box>
        )}

        {/* HPO licence: the release behind these suggestions. Phenotypes and
            diseases both come from the HPO network; genes do not, so a
            genes-only list carries no HPO line. */}
        {hpoDerived && (
          <HpoReleaseNote release={hpoRelease} sx={{ px: 0, mt: 1 }} />
        )}
      </Box>
    </Grid>
  );
}
